<template>
  <aside class="minicart">
    <header>
      Carrinho
      <span class="minicart__count">
        {{ itemsCount }}
      </span>
    </header>
    <article>
      <ul v-if="visibleItems.length">
        <li
          v-for="(item, i) in visibleItems"
          :key="i"
          class="minicart__item"
          :class="item.is_out_of_stock && 'minicart__item--out-of-stock'"
          :data-line-item-id="item.line_item_id"
        >
          <component
            :is="item.is_gift ? 'span' : 'a'"
            v-if="item.image_url"
            :href="getItemLink(item)"
            class="minicart__item-img-wrapper"
          >
            <img :src="item.image_url" loading="lazy" />
          </component>
          <div class="minicart__item-content">
            <div v-if="item.is_gift">
              <div class="minicart__item-gift">
                <icon-gift />
                <span>Brinde</span>
                <choosable-gift-btn
                  v-if="item.variant_id && item.line_item_id"
                  :current-variant-id="Number(item.variant_id)"
                  :line-item-id="Number(item.line_item_id)"
                  :choosable-gifts="getChoosableGifts(item)"
                  @newVariant="
                    (gift) => changeGiftVariant({ itemId: item.item_id, gift })
                  "
                />
              </div>
            </div>
            <a
              v-if="!item.is_gift"
              :href="getItemLink(item)"
              class="minicart__item-name"
            >
              <span>{{ item.name }}</span>
              <small>
                {{ getItemCaseDevice(item) || '&nbsp;' }}
              </small>
            </a>
            <span v-else class="minicart__item-name">
              <span>
                <b v-if="item.quantity > 1">{{ item.quantity }}x</b>
                {{ item.name }}
              </span>
            </span>
            <div
              v-if="!item.is_gift && !item.is_out_of_stock"
              class="minicart__item-controls"
            >
              <div class="minicart__item-prices">
                <del v-if="item.old_price">
                  {{ formatPrice(item.old_price) }}
                </del>
                <strong>
                  {{ formatPrice(item.price) }}
                </strong>
              </div>
              <custom-quantity-input
                :initial-quantity="item.quantity"
                :has-title="false"
                :is-input-readonly="false"
                @quantityFixed="
                  (quantity) =>
                    debouncedSetItemQty({ itemId: item.item_id, quantity })
                "
              />
            </div>
            <div v-if="item.is_out_of_stock" class="minicart__item-alert">
              Sem estoque
            </div>
          </div>
          <button
            v-if="!item.is_gift"
            class="minicart__item-delete"
            @click.prevent.stop="
              setItemQty({ itemId: item.item_id, quantity: 0 })
            "
          >
            <icon-trash size="18px" />
          </button>
        </li>
      </ul>
      <div v-else class="minicart__empty">
        <span>Seu carrinho está vazio</span>
        <button
          type="button"
          class="btn btn--large btn--block"
          @click.prevent="toggle"
        >
          Continuar comprando
        </button>
      </div>
    </article>
    <footer v-if="visibleItems.length">
      <div class="minicart__alert">
        <fade slide="down">
          <div v-if="!isGiftsMatching" class="minicart__alert-gifts">
            <span v-if="isServerSyncing">Carregando seus brindes... 🎁</span>
            <span v-else>Tem brindes pra você no checkout 🎁</span>
            <three-dots-loading v-if="isServerSyncing" />
          </div>
        </fade>
      </div>
      <div v-if="totalItemsDiscount" class="minicart__discount">
        <div class="minicart__discount-label">Descontos</div>
        <div>
          <strong>
            -
            {{ formatPrice(totalItemsDiscount) }}
          </strong>
        </div>
      </div>
      <div class="minicart__subtotal">
        <div class="minicart__subtotal-label">Subtotal</div>
        <div>
          <strong>
            {{ formatPrice(discountedSubtotal) }}
          </strong>
          <span v-if="taxFreeInstallments > 1">
            {{ taxFreeInstallments }}x de
            {{ formatPrice(discountedSubtotal / taxFreeInstallments) }}
            sem juros
          </span>
        </div>
      </div>
      <div v-if="missingForFreeShipping !== null" class="minicart__progress">
        <div class="minicart__progress-label">
          <span v-if="missingForFreeShipping">
            Adicione {{ formatPrice(missingForFreeShipping) }} para
            <b>frete grátis</b>
          </span>
          <b v-else>Você ganhou frete grátis!</b>
        </div>
        <div class="minicart__progress-bar">
          <div
            class="minicart__progress-fill"
            :style="{
              width: `${percentageForFreeShipping || 5}%`,
            }"
          />
        </div>
      </div>
      <div v-else-if="storeInfo.store === 'br'">
        <fade slide="down">
          <div class="minicart__shipping">
            <a
              v-if="!isShippingCalculatorOpen"
              href="#"
              @click.prevent.stop="isShippingCalculatorOpen = true"
            >
              Calcular frete
            </a>
            <template v-else>
              <three-dots-loading v-if="!canCalculateShipping" />
              <fade slide="down">
                <shipping-estimate
                  v-if="canCalculateShipping"
                  :cart-number="orderNumber"
                  :initial-zipcode="zipCode"
                  @zipcode="setZipCode"
                />
              </fade>
            </template>
          </div>
        </fade>
      </div>
      <div>
        <p class="minicart__coupon">
          <strong>Possui um cupom de desconto?</strong>
          Você poderá adiciona-lo no checkout. ;)
        </p>
        <div class="minicart__cta-wrapper">
          <button
            type="button"
            class="btn btn--large btn--green btn--block minicart__cta"
            :disabled="isWaitingToCheckout"
            @click.prevent="isWaitingToCheckout = true"
          >
            <template v-if="!isWaitingToCheckout">Finalizar compra</template>
            <template v-else>
              <span
                class="minicart__cta-progress"
                :style="ctaWaitingProgress && `width: ${ctaWaitingProgress}%`"
              ></span>
              <three-dots-loading />
            </template>
          </button>
        </div>
      </div>
      <a href="#" class="minicart__back" @click.prevent="toggle">
        &#8249;
        <span>Continuar comprando</span>
      </a>
    </footer>
  </aside>
</template>

<script>
/* eslint-disable no-console, camelcase */
/**
 * @typedef {import('~/store/minicart').CartItem} CartItem
 */
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import debounce from '~/utils/debounce'
import requestIdleCallback from '~/utils/requestIdleCallback'
import { redirectToCheckout } from '~/utils/toCheckout'
import priceable from '~/mixins/priceable'
import Fade from '~/components/Fade.vue'
import IconTrash from '~/components/icons/trash/Trash'
import IconGift from '~/components/icons/gift-box/GiftBox'
import ThreeDotsLoading from '~/components/loadings/three-dots/ThreeDotsLoading.vue'
import CustomQuantityInput from '~/components/product/form/custom-quantity-input/CustomQuantityInput.vue'
import ShippingEstimate from '~/components/cart/shipping-estimate/ShippingEstimate.vue'
import ChoosableGiftBtn from '~/components/line-item/line-item-actions/choosable-gift-btn/ChoosableGiftBtn.vue'

export default {
  name: 'Minicart',
  components: {
    Fade,
    IconTrash,
    IconGift,
    ThreeDotsLoading,
    CustomQuantityInput,
    ShippingEstimate,
    ChoosableGiftBtn,
  },

  mixins: [priceable],

  data() {
    return {
      isOpenOnce: false,
      isLoadingPromotions: false,
      promotions: null,
      isShippingCalculatorOpen: false,
      isWaitingToCheckout: false,
      ctaWaitingProgress: null,
      fallbackSyncInterval: null,
      timeouts: [],
    }
  },

  computed: {
    ...mapGetters(['storeInfo']),
    ...mapState('minicart', [
      'orderNumber',
      'zipCode',
      'isOpen',
      'isServerSyncing',
      'lastSyncAt',
    ]),
    ...mapGetters('minicart', [
      'isEnabled',
      'isServerSync',
      'isGiftsMatching',
      'activeItems',
      'itemsCount',
      'subtotal',
      'visibleItems',
    ]),

    canCalculateShipping() {
      return this.orderNumber && this.isServerSync
    },

    priceForFreeShipping() {
      /*
      @TODO: The returned free shipping rules do not match the
      calculated shipping, free shipping appears to be granted to any
      device ID on some amount and zip code, but no rules match.
      ***
      const openFreeShipping = this.getOpenPromotionRule(
        this.promotions?.free_shipping
      )
      */
      return null
    },

    missingForFreeShipping() {
      if (this.priceForFreeShipping === null) return null
      if (this.priceForFreeShipping >= 0) {
        return Math.max(0, this.priceForFreeShipping - this.subtotal)
      }
      return null
    },

    percentageForFreeShipping() {
      if (!this.priceForFreeShipping) return 0
      if (this.missingForFreeShipping === null) return 0
      if (this.missingForFreeShipping <= 0) return 100
      return (
        100 -
        Math.round(
          (this.missingForFreeShipping * 100) / this.priceForFreeShipping
        )
      )
    },

    taxFreeInstallments() {
      const openFreeInstallments = this.getOpenPromotionRule(
        this.promotions?.free_installments
      )
      if (!openFreeInstallments) return 1
      const { attributes } = openFreeInstallments.rules[0]
      const minAmount = Number(attributes.amount_min)
      if (minAmount > 0 && minAmount <= this.subtotal) {
        const installmentsNumber = openFreeInstallments.free_installments
        if (installmentsNumber > 1) {
          return installmentsNumber
        }
      }
      return 1
    },

    totalItemsDiscount() {
      if (!this.promotions) return 0
      const cheapestItemDiscounts = this.promotions.cheapest_item_discount
      if (!Array.isArray(cheapestItemDiscounts)) return 0
      let discount = 0
      cheapestItemDiscounts.forEach((cheapestItemDiscount) => {
        cheapestItemDiscount.discount_rules?.forEach((discountRule) => {
          const minQuantity = Number(discountRule.quantity_trigger)
          const discountPct = Number(discountRule.discount)
          const isCumulative =
            Number(discountRule.cumulative) > 0 ||
            discountRule.cumulative !== false
          if (isNaN(minQuantity) || !discountPct) return
          const eligibleItems = this.getDiscountableItems(
            discountRule,
            this.visibleItems
          )
          if (eligibleItems.length >= minQuantity) {
            const applicableSets = Math.floor(
              eligibleItems.length / minQuantity
            )
            const maxSets = isCumulative ? applicableSets : 1
            const sortedItems = eligibleItems.sort((a, b) => a.price - b.price)
            for (let set = 0; set < maxSets; set++) {
              const startIndex = set * minQuantity
              const itemsInSet = sortedItems.slice(
                startIndex,
                startIndex + minQuantity
              )
              const itemsToDiscount = itemsInSet.slice(
                0,
                Number(discountRule.quantity_discount || 1)
              )
              itemsToDiscount.forEach((item) => {
                const itemDiscount = (item.price * discountPct) / 100
                discount += itemDiscount
              })
            }
          }
        })
      })
      return discount
    },

    discountedSubtotal() {
      return this.subtotal - this.totalItemsDiscount
    },
  },

  watch: {
    isEnabled: {
      handler(isEnabled) {
        if (!isEnabled) return
        requestIdleCallback(() => {
          this.loadPromotions()
        })
      },
      immediate: true,
    },

    isOpen(isOpen) {
      this.isOpenOnce = true
      if (!isOpen) return
      this.emitGtagEvent('open_minicart')
    },

    isOpenOnce(isOpenOnce) {
      if (!isOpenOnce) return
      this.fallbackSyncInterval = setInterval(async () => {
        /* This fallback exists mainly because server sets gifts asynchronously,
         sometimes returned items on fresh server cart doesn't match expected gifts
         and gifts are present on next sync (after some seconds). */
        if (!this.isOpen) return
        if (this.isServerSync && this.isGiftsMatching) return
        if (this.isServerSyncing) return
        await this.syncWithServer()
        this.$nextTick(() => {
          if (this.isServerSync && this.isGiftsMatching) return
          // Prevent infinite loop (never, but...)
          clearInterval(this.fallbackSyncInterval)
        })
      }, 6000)
      if (this.isServerSyncing) return
      if (this.lastSyncAt) {
        if (Date.now() - this.lastSyncAt < 60000) return
      }
      this.syncWithServer()
    },

    itemsCount(itemsCount) {
      if (itemsCount === 0 && this.isOpen) {
        this.toggle()
      } else {
        this.checkItemGifts(this.promotions)
      }
    },

    promotions(promotions) {
      this.checkItemGifts(promotions)
    },

    isShippingCalculatorOpen(isShippingCalculatorOpen) {
      if (!isShippingCalculatorOpen) return
      this.checkServerSyncing()
    },

    isWaitingToCheckout(isWaitingToCheckout) {
      if (!isWaitingToCheckout) return
      const updateCtaProgress = () => {
        if (this.ctaWaitingProgress >= 80) return
        if (!this.ctaWaitingProgress) {
          this.ctaWaitingProgress = 40
        } else {
          this.ctaWaitingProgress += 20
        }
        this.timeouts.push(setTimeout(updateCtaProgress, 4000))
      }
      this.timeouts.push(setTimeout(updateCtaProgress, 2000))
      this.emitGtagEvent('minicart_to_checkout')
      // Fallback for too slow cart API operations
      this.timeouts.push(setTimeout(() => this.goToCheckout(), 30000))
      const itemsOutOfStock = this.visibleItems.filter((
        /** @type {CartItem} */ item
      ) => {
        return item.is_out_of_stock
      })
      if (itemsOutOfStock.length) {
        itemsOutOfStock.forEach((/** @type {CartItem} */ item) => {
          this.setItemQty({ itemId: item.item_id, quantity: 0 })
        })
        return
      }
      if (this.isServerSync) {
        this.goToCheckout()
        return
      }
      this.checkServerSyncing()
    },

    isServerSyncing() {
      if (this.isShippingCalculatorOpen || this.isWaitingToCheckout) {
        this.checkServerSyncing()
      }
    },

    isServerSync(isServerSync) {
      if (!isServerSync) return
      if (this.isWaitingToCheckout) this.goToCheckout()
    },
  },

  beforeDestroy() {
    if (this.fallbackSyncInterval) {
      clearInterval(this.fallbackSyncInterval)
    }
    this.timeouts.forEach((timeout) => {
      clearTimeout(timeout)
    })
  },

  methods: {
    ...mapMutations('minicart', ['toggle']),
    ...mapActions('minicart', [
      'setCartInfo',
      'syncWithServer',
      'setItemQty',
      'setItemEligibleGifts',
      'changeGiftVariant',
    ]),

    emitGtagEvent(name, params = {}) {
      this.$store.dispatch('analytics/emitGtagEvent', { name, params })
    },

    getItemLink(/** @type {CartItem} */ item) {
      return item.slug ? `/${item.slug}/p/` : '#'
    },

    getItemCaseDevice(/** @type {CartItem} */ item) {
      if (item.case_device) return item.case_device
      if (item.device?.name) {
        if (item.case_type?.name) {
          return `${item.device.name} (${item.case_type.name})`
        }
        return item.device.name
      }
      if (item.case_type?.name) {
        return item.case_type.name
      }
      return null
    },

    getChoosableGifts(/** @type {CartItem} */ giftItem) {
      if (!giftItem.gift_for_item_id) return null
      /** @type {CartItem[]} */
      const activeItems = this.activeItems
      const cartItem = activeItems.find((item) => {
        return item.item_id === giftItem.gift_for_item_id
      })
      return cartItem?.eligible_gifts || null
    },

    debouncedSetItemQty: debounce(function (opt) {
      this.setItemQty(opt)
    }, 300),

    async loadPromotions() {
      this.isLoadingPromotions = true
      const params = { ...this.storeInfo }
      try {
        this.promotions = await this.$axios.$get(`/promotions`, { params })
      } catch {
        //
      }
      this.isLoadingPromotions = false
    },

    getOpenPromotionRule(promotions) {
      // Find promotion rule by amount only wihout product/device filters
      return promotions?.find((promotion) => {
        if (!Array.isArray(promotion?.rules) || promotion.rules.length > 1)
          return false
        const [rule] = promotion.rules
        return Boolean(rule?.name === 'adjusted_total')
      })
    },

    getDiscountableItems(discountRule, cartItems) {
      return cartItems
        .filter((/** @type {CartItem} */ item) => {
          if (discountRule.product_type_codes) {
            return discountRule.product_type_codes.includes(
              item.product_type_code
            )
          }
          return true
        })
        .flatMap((item) => {
          return Array(item.quantity).fill(item)
        })
    },

    checkItemGifts(promotions) {
      if (!promotions) return null
      const choosableGifts = promotions.choosable_gifts
      if (!Array.isArray(choosableGifts)) return 0
      this.visibleItems.forEach((/** @type CartItem */ item) => {
        if (item.is_gift) return
        const eligibleGifts = []
        choosableGifts.forEach((choosableGift) => {
          const giftRules = choosableGift?.gift_rules
          if (!giftRules || typeof giftRules !== 'object') return false
          const { gifts } = giftRules
          if (!Array.isArray(gifts) || !gifts.length) return false
          if (
            Array.isArray(giftRules.materials) &&
            giftRules.materials.length
          ) {
            const itemMaterial = `${item.case_type?.code}-${item.device?.code}`
            if (
              !giftRules.materials.some((material) => material === itemMaterial)
            )
              return false
          }
          if (
            Array.isArray(giftRules.product_types) &&
            giftRules.product_types.length
          ) {
            if (!giftRules.product_types.includes(item.product_type_code))
              return false
          }
          if (
            Array.isArray(giftRules.unsupported_product_ids) &&
            giftRules.unsupported_product_ids.length
          ) {
            if (giftRules.unsupported_product_ids.includes(item.product_id))
              return false
          }
          gifts.forEach((gift) => eligibleGifts.push(gift))
        })
        this.setItemEligibleGifts({
          itemId: item.item_id,
          gifts: eligibleGifts,
        })
      })
    },

    setZipCode(zipCode) {
      this.setCartInfo({ zipCode })
    },

    checkServerSyncing() {
      if (this.isServerSync || this.isServerSyncing) return
      this.syncWithServer()
    },

    goToCheckout() {
      this.ctaWaitingProgress = 100
      this.$nextTick(() => {
        redirectToCheckout({ ...this.$config, ...this.$store.state })
      })
    },
  },
}
</script>

<style lang="scss">
@import '~/assets/css/btn.scss';

.minicart {
  display: flex;
  flex-direction: column;
  height: 100dvh;
  > header {
    height: 52px;
    background: #f9fafb;
    font-size: 17px;
    font-weight: 500;
    color: $v4_color_text;
    text-transform: uppercase;
    line-height: 52px;
    padding-left: 1.5rem;
  }
  > article {
    flex-grow: 1;
    overflow-y: auto;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
  > footer {
    position: relative;
    padding: 1rem 1rem 0.75rem;
    background: #f9fafb;
    border-top: 2px solid #f3f4f6;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.08);
    .minicart__cta {
      position: relative;
      text-transform: uppercase;
      transition: background-color 0.15s ease;
      min-height: 48px;
      flex-direction: column;
      overflow: hidden;
      &:disabled {
        cursor: auto;
      }
      .spinner {
        margin: 0 auto;
        z-index: 10;
      }
    }
    .minicart__cta-progress {
      position: absolute;
      display: block;
      height: 100%;
      left: 0;
      top: 0;
      width: 20%;
      border-radius: 4px;
      background: darken($v4_success_green, 15%);
      transition: width 0.2s ease;
    }
  }
  &__count {
    background: darken($v4_color_line_lighter, 5%);
    width: 32px;
    line-height: 32px;
    font-size: 16px;
    border-radius: 50px;
    display: inline-block;
    text-align: center;
    font-weight: 600;
    color: $v4_color_blue;
    margin-left: 0.75rem;
  }
  &__item {
    position: relative;
    display: flex;
    column-gap: 1rem;
    &:not(:last-child) {
      border-bottom: 2px solid $v4_color_gray;
    }
    img {
      width: 6rem;
      height: 6rem;
      border-radius: 4px;
    }
    &--out-of-stock {
      .minicart__item-name,
      img {
        opacity: 0.7;
      }
    }
  }
  &__item-img-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  &__item-content {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;
    row-gap: 0.5rem;
    color: $v4_color_text;
  }
  &__item-gift {
    display: inline-flex;
    align-items: center;
    column-gap: 0.75rem;
    font-weight: 500;
    border-radius: 100px;
    background: $v4_color_line_lighter;
    padding: 0.5rem 1.5rem;
    > svg {
      height: 16px;
      width: auto;
      fill: $v4_success_green;
    }
    > span {
      color: rgba($v4_color_text, 0.9);
      text-transform: lowercase;
    }
    .choosable-gift__btn {
      font-size: 92%;
      color: rgba($v4_color_text, 0.9);
      border: none;
      text-decoration: underline;
      text-underline-offset: 3px;
      &:hover {
        background: darken($v4_color_line_lighter, 5%);
      }
    }
  }
  &__item-name {
    max-width: 180px;
    margin-right: 28px;
    color: $v4_color_text;
    text-decoration: none;
    text-underline-offset: 4px;
    transition: color 0.15s ease;
    text-wrap: balance;
    small {
      font-size: 90%;
      display: block;
      font-weight: 500;
      color: rgba($v4_color_text, 0.6);
      text-decoration: none !important;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
    b {
      display: inline-block;
      margin-right: 0.25rem;
    }
  }
  &__item-name:hover:is(a) span,
  &__item:has(img:hover) .minicart__item-name:is(a) span {
    color: $v4_color_blue;
    text-decoration: underline;
  }
  &__item-controls {
    display: flex;
    flex-wrap: wrap;
    row-gap: 0.5rem;
    column-gap: 1rem;
    justify-content: space-between;
    align-items: flex-start;
  }
  &__item-prices {
    flex-grow: 1;
    display: flex;
    align-items: center;
    column-gap: 0.5rem;
    strong {
      font-weight: 500;
      color: $v4_color_primary;
      font-size: 105%;
    }
    del {
      font-weight: 300;
      font-size: 90%;
    }
    @media (min-width: $v4_tablet) {
      flex-direction: column;
      align-items: flex-start;
    }
  }
  &__item-alert {
    font-size: 110%;
    font-weight: 600;
  }
  &__item-delete {
    position: absolute;
    z-index: 3;
    top: 1rem;
    right: 1rem;
    background: #fee2e2;
    border: none;
    outline: none;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50px;
    transition: background-color 0.15s ease;
    cursor: pointer;
    svg {
      fill: #991b1b;
    }
    &:hover {
      background: #dc2626;
      svg {
        fill: #fee2e2;
      }
    }
  }
  .add-to-cart {
    &__quantity-container {
      margin: 0;
    }
    &__user-quantity {
      background: #fff;
      outline: none;
      border: 2px solid #eee !important;
      position: relative;
      z-index: 2;
      border-radius: 3px;
    }
    &__quantity-decrease,
    &__quantity-increase {
      border: none;
      opacity: 0.85;
      &:hover {
        background: #fff;
        opacity: 1;
      }
      &:disabled {
        opacity: 0.3;
      }
    }
  }
  &__item,
  &__crossell {
    padding: 1rem 1.5rem;
  }
  &__crossell {
    overflow: hidden;
    .description {
      display: none;
    }
  }
  &__subtotal,
  &__discount {
    color: $v4_color_text;
    margin-bottom: 0.25rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 1rem;
    text-align: right;
  }
  &__subtotal strong {
    font-size: 1.5rem;
    font-weight: 600;
    display: block;
  }
  &__discount strong {
    font-size: 1.2rem;
    font-weight: 600;
    display: block;
    color: $v4_color_primary;
  }
  &__subtotal-label {
    font-size: 1.1rem;
    color: rgba($v4_color_text, 0.8);
  }
  &__discount-label {
    font-size: 1.1rem;
    color: rgba($v4_color_primary, 0.8);
  }
  &__back {
    display: inline-block;
    margin-top: 0.5rem;
    color: rgba($v4_color_text, 0.8);
    font-size: 1rem;
    font-weight: 500;
    text-decoration: none;
    transition: color 0.15s ease;
    span {
      margin-left: 0.25rem;
    }
    &:hover span {
      color: $v4_color_text;
    }
  }
  &__progress {
    b {
      font-weight: 600;
    }
  }
  &__progress-label {
    display: flex;
    column-gap: 1rem;
    justify-content: space-between;
  }
  &__progress-bar {
    height: 12px;
    border-radius: 20px;
    overflow: hidden;
    background: $v4_color_gray;
    margin-top: 0.25rem;
    margin-bottom: 1rem;
  }
  &__progress-fill {
    height: 12px;
    border-radius: 20px;
    background: $v4_color_primary;
    transition: all 500ms ease-in-out;
  }
  &__progress a,
  &__shipping a {
    font-weight: 500;
    color: $v4_color_text;
    text-underline-offset: 3px;
  }
  &__shipping {
    display: flex;
    justify-content: flex-end;
    margin: 0.75rem 0;
    position: relative;
    min-height: 20px;
    .spinner {
      margin-top: 5px;
      margin-right: 20px;
      &:not(:only-child) {
        display: none;
      }
    }
  }
  .cart__shipping {
    padding-top: 0;
    padding-bottom: 0;
    flex-grow: 1;
    &-code {
      margin-top: 0;
      margin-bottom: 0;
    }
    &-label {
      display: none !important;
    }
    &-zipcode {
      padding-left: 1rem;
      padding-right: 0.5rem;
    }
    &__rates {
      width: 100%;
    }
    &-fields {
      margin-top: 0;
      margin-bottom: 0;
    }
  }
  &__coupon {
    display: block;
    background: rgba($v4_success_green, 0.07);
    padding: 0.7rem 1rem calc(0.5rem + 5px);
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    margin-top: 0;
    margin-bottom: -5px;
    strong {
      font-weight: 500;
      display: block;
      color: darken($v4_color_text, 15%);
    }
  }
  &__alert {
    display: block;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    transform: translateY(-100%);
    background: #f3f4f6;
    color: darken($v4_color_text, 15%);
    font-weight: 500;
    font-size: 1.1rem;
    padding: 0.75rem 1rem;
    &:empty {
      display: none;
    }
  }
  &__alert-gifts {
    display: flex;
    align-items: center;
    column-gap: 0.75rem;
    justify-content: space-between;
    padding-right: 15px;
  }
  &__empty {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    row-gap: 1rem;
    font-size: 1.5rem;
    padding: 2rem;
    height: 100%;
  }
}
</style>
